/**
 * Login/session bootstrap for the Sonata Chat shell.
 *
 * Extracted from the inline <script> that used to live in index.html, so this
 * code is part of the normal build (bundled, checked, minified) instead of
 * living as unbundled classic-script code pasted into the HTML.
 *
 * `CURRENT_CONFIG`, `updateEndpoints`, `forceLogout` and `startChat` are
 * exposed on `window` because the remaining Converse plugins still registered
 * inline in index.html (contact sync, bookmark sync, etc.) reference them as
 * bare globals. Extracting those plugins into their own modules is a
 * separate follow-up, not part of this pass.
 */

/* global converse */

import QRCode from 'qrcode';

let IS_CONVERSE_INITIALIZING = false;

// The wake/reconnect-detection code far below calls `converse.api.ping(...)`,
// but `converse` (window.converse, the public facade built up by
// @converse/headless's public.js) never actually exposes an `.api` property
// -- only individual plugins get a private `api` handed to them as `this.api`
// inside their own initialize(). sonata-auth.js is bundled as its own
// separate rspack entry (dist/sonata-auth.js, see rspack.build.js), so it
// can't just `import { api } from '@converse/headless'` either -- that would
// bundle a second, disconnected copy of headless with its own uninitialized
// _converse, not the one converse.min.js's bundle actually connects. The
// sonata-debug plugin below (whitelisted and running) captures the real
// one into this variable. (sonata-reconnect-ui used to be missing from
// startChat()'s whitelisted_plugins and so never initialized; it is
// whitelisted now.)
let _api = null;

const CURRENT_CONFIG = {
    domain: '',
    xmpp_host: '',
    api_base: '',
    ws_url: '',
};

function updateEndpoints(selectedDomain, selectedCode) {
    CURRENT_CONFIG.domain = selectedDomain;
    CURRENT_CONFIG.server_code = selectedCode;
    CURRENT_CONFIG.xmpp_host = `chat.${selectedDomain}`;
    CURRENT_CONFIG.api_base = `https://api-admin-chat.${selectedDomain}`;
    CURRENT_CONFIG.ws_url = `wss://chat.${selectedDomain}/websocket`;
    localStorage.setItem('s_selected_domain', selectedDomain);
    localStorage.setItem('s_selected_domain_code', selectedCode);
}

function getDeviceId() {
    let dId = localStorage.getItem('sonata_device_id');
    if (!dId) {
        dId = 'web-' + Date.now();
        localStorage.setItem('sonata_device_id', dId);
    }
    return dId;
}

/* =========================
   S-Chat-Token (this client's OMEMO device id)
   =========================
   iOS builds from 2026-10-12 call /token/list after login and REMOVE from the
   account's OMEMO device list every rid that isn't the chatToken of an active
   backend session (they skip this only if some session has no chatToken).
   chatToken is whatever the client sends as S-Chat-Token. If Web sent a
   wrong/stale value, iOS would drop Web's rid, nobody would encrypt to Web,
   and Web re-adding itself would loop. So: send our CURRENT rid on every
   backend request once OMEMO has given us one -- like iOS, except on
   /token/create, where no rid exists yet. Read live from the OMEMO store each
   time, so a regenerated rid is picked up immediately. */
let _converseForChatToken = null; // set by the sonata-debug plugin below

function currentChatToken() {
    const id = _converseForChatToken?.state?.omemo_store?.get('device_id');
    return id ? String(id) : null;
}

function chatTokenHeader() {
    const token = currentChatToken();
    return token ? { 'S-Chat-Token': token } : {};
}
// Used by the backend calls still inlined in index.html / fullscreen.html.
window.sonataChatTokenHeader = chatTokenHeader;

/**
 * @param {string} dId
 * @param {{ chatToken?: boolean }} [opts] - chatToken:false for /token/create
 */
function authHeaders(dId, { chatToken = true } = {}) {
    return {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'S-Device-Id': dId,
        'S-Device-Platform': 'Web',
        'S-Device-Model': 'Web Browser',
        ...(chatToken ? chatTokenHeader() : {}),
    };
}

function apiRoot() {
    return CURRENT_CONFIG.api_base.replace(/\/api$/, '').replace(/\/$/, '');
}

/* =========================
   TOKEN REFRESH
   ========================= */

let refreshTimer = null;

function scheduleTokenRefresh(expiresIn) {
    if (refreshTimer) clearTimeout(refreshTimer);
    const delay = (expiresIn - 30) * 1000;
    refreshTimer = setTimeout(refreshSonataToken, Math.max(delay, 5000));
}

async function refreshSonataToken() {
    const refreshToken = localStorage.getItem('s_refresh_token');
    const login = localStorage.getItem('s_login');
    const dId = localStorage.getItem('sonata_device_id');

    if (!refreshToken || !login) return;

    try {
        const res = await fetch(`${CURRENT_CONFIG.api_base}/token/refresh`, {
            method: 'POST',
            headers: authHeaders(dId),
            body: JSON.stringify({
                refresh_token: refreshToken,
                login: login,
            }),
        });

        const data = await res.json();

        if (res.ok && data.access_token) {
            sessionStorage.setItem('s_token', data.access_token);
            localStorage.setItem('s_refresh_token', data.refresh_token);
            scheduleTokenRefresh(data.expires_in);
        } else if (res.status === 401 || res.status === 403 || res.status === 400) {
            console.error('Critical Auth Error:', data.message);
            forceLogout();
        }
    } catch (err) {
        console.error('Network error during refresh:', err);
        setTimeout(refreshSonataToken, 30000);
    }
}

function forceLogout() {
    sessionStorage.clear();
    localStorage.removeItem('s_refresh_token');
    clearPersistedCredentials();

    try {
        if (converse.plugins.get('sonata-state-manager')) {
            converse.plugins.get('sonata-state-manager').logout();
        } else {
            window.location.reload();
        }
    } catch (e) {
        window.location.reload();
    }
}

/* =========================
   CREDENTIAL PERSISTENCE (Electron only)
   =========================
   sessionStorage (s_jid/s_pass/s_token) doesn't survive a full app restart
   (OS reboot/shutdown kills the renderer process), so a plain sessionStorage
   check on boot always falls through to the login form after one. In the
   Electron shell (window.electronAuth, exposed by electron/preload.js), the
   password is additionally kept in localStorage encrypted via OS-level
   safeStorage (DPAPI/Keychain) so login can survive a restart. In a plain
   browser tab (no window.electronAuth) none of this runs and behaviour is
   unchanged: closing the tab still requires logging in again. */

async function persistCredentials(jid, password, login, authType = 'password') {
    if (!window.electronAuth) return;
    try {
        const encrypted = await window.electronAuth.encrypt(password);
        if (!encrypted) return;
        localStorage.setItem('s_jid_persist', jid);
        localStorage.setItem('s_pass_enc', encrypted);
        // Not sensitive (just the username, not the password) — no need for
        // safeStorage encryption, plain localStorage is fine. Without this,
        // restoring a session via persisted credentials (below) never sets
        // s_login at all: the profile modal's "Логін" field renders empty,
        // and refreshSonataToken() (which requires it) silently no-ops,
        // meaning the access token would never refresh on such a session.
        if (login) localStorage.setItem('s_login_persist', login);
        // QR logins persist the access_token in place of a real password
        // (see startChat's useOAuth) -- remembered here so a restored session
        // knows to authenticate via SASL X-OAUTH2 instead of SCRAM/PLAIN.
        localStorage.setItem('s_auth_type_persist', authType);
    } catch (e) {
        console.error('Failed to persist credentials:', e);
    }
}

async function loadPersistedCredentials() {
    if (!window.electronAuth) return null;
    const jid = localStorage.getItem('s_jid_persist');
    const encrypted = localStorage.getItem('s_pass_enc');
    if (!jid || !encrypted) return null;

    try {
        const password = await window.electronAuth.decrypt(encrypted);
        if (!password) return null;
        return {
            jid,
            password,
            login: localStorage.getItem('s_login_persist'),
            authType: localStorage.getItem('s_auth_type_persist') || 'password',
        };
    } catch (e) {
        console.error('Failed to restore persisted credentials:', e);
        return null;
    }
}

function clearPersistedCredentials() {
    localStorage.removeItem('s_jid_persist');
    localStorage.removeItem('s_pass_enc');
    localStorage.removeItem('s_login_persist');
    localStorage.removeItem('s_auth_type_persist');
}

/* =========================
   CONVERSE STARTUP
   ========================= */

function startChat(jid, password, token, useOAuth = false) {
    console.log('=== DEBUG: START CHAT ===');
    console.log('JID:', jid);
    console.log('Password Length:', password ? password.length : 0);
    console.log('WS URL:', CURRENT_CONFIG.ws_url);
    console.log('XMPP Host:', CURRENT_CONFIG.xmpp_host);

    console.log('Checking for double initialization...', IS_CONVERSE_INITIALIZING);
    if (IS_CONVERSE_INITIALIZING) {
        console.warn('⚠️ Попытка двойного запуска Converse пресечена.');
        return;
    }

    // Проверка наличия JID (бывает при пустых sessionStorage)
    if (!jid || jid === 'null') return;

    // 3. Генерация стабильного ресурса
    const storedId = localStorage.getItem('sonata_device_id') || 'web-' + Date.now();
    if (!localStorage.getItem('sonata_device_id')) {
        localStorage.setItem('sonata_device_id', storedId);
    }
    const tabId = Math.random().toString(36).substring(2, 10); // Уникально для каждой вкладки
    const stableResource = 'Web-' + tabId;

    const bareJid = jid.split('/')[0];
    const fullJid = `${bareJid}/${stableResource}`;

    // 5. Очистка сессии при смене ресурса (чтобы избежать "User session not found")
    if (sessionStorage.getItem('last_active_resource') !== stableResource) {
        console.warn('--- Смена ресурса, очистка кэша сессии ---');
        Object.keys(sessionStorage).forEach((key) => {
            if (key.startsWith('converse')) sessionStorage.removeItem(key);
        });
        sessionStorage.setItem('last_active_resource', stableResource);
    }

    IS_CONVERSE_INITIALIZING = true;
    if (token) sessionStorage.setItem('s_token', token);
    sessionStorage.setItem('s_auth_type', useOAuth ? 'oauth' : 'password');
    document.getElementById('sonata-auth-overlay').style.display = 'none';
    document.getElementById('sonata-loader').style.display = 'flex';

    // QR logins hand us an access_token instead of the account's real
    // password, and ejabberd rejects that under SCRAM/PLAIN (it validates
    // those against the real password's verifier). The server advertises a
    // SASL X-OAUTH2 mechanism specifically for token-based auth -- Strophe
    // ships an implementation (Strophe.SASLXOAuth2) but won't pick it on its
    // own since SCRAM outranks it by default priority, so it's whitelisted
    // as the *only* mechanism here to force its use for this connection.
    const connection_options = { disable_host_meta: false };
    if (useOAuth) {
        // converse.env (and Strophe with it) is only populated once the full
        // converse module has been required -- before the very first
        // converse.initialize() call in the page's lifetime, `converse` is
        // still entry.js's lightweight stub and `.env` is undefined. Force
        // that load now so Strophe.SASLXOAuth2 is resolvable; converse.load()
        // is a no-op on subsequent calls (module cache), and this only runs
        // it if it hasn't happened yet, so it can't re-trigger entry.js's
        // buffered-plugin replay and hit "plugin already registered".
        if (!converse.env) converse.load();
        connection_options.mechanisms = [converse.env.Strophe.SASLXOAuth2];
    }

    converse.initialize({
        muc_domain: `conference.${CURRENT_CONFIG.xmpp_host}`,
        websocket_url: CURRENT_CONFIG.ws_url,
        locked_domain: CURRENT_CONFIG.xmpp_host,
        theme: 'classic',
        view_mode: 'fullscreen',
        i18n: 'uk',
        jid: fullJid,
        password: password,
        authentication: 'login',
        auto_login: true,
        auto_authenticate: true,
        cache_password: true,
        discover_connection_methods: false,
        connection_options,
        keepalive: true,
        // Was `false` since the original vendor commit, with no documented
        // reason anywhere in this repo's history, overriding converse's own
        // default of `true` (default since v6.0.0). Without XEP-0198 Stream
        // Management, every reconnect is a cold rebind: the server has no
        // reason to hold and redeliver anything that arrived for us while
        // the socket was down but not yet detected as dead (see the
        // ping/online/wake-detection comments below -- that detection gap
        // is up to ~60s on a silent blip with no OS-level signal). A `<resume/>`-
        // capable reconnect asks the server to replay exactly that gap
        // instead of relying solely on the MAM catch-up that runs afterward.
        // Falls back to a normal (non-resumed) reconnect automatically if
        // the server doesn't advertise `<sm/>` support, so this is safe even
        // if XEP-0198 turns out not to be enabled server-side -- confirm
        // that separately before relying on it, and verify with a real
        // "drop wifi for a few seconds mid-conversation" test.
        enable_smacks: true,
        auto_reconnect: true,
        resource: stableResource,
        allow_resource_override: false,
        whitelisted_plugins: [
            'openmoji-github-adapter',
            'converse-muc',
            'sonata-branding',
            'converse-receipts',
            'sonata-state-manager',
            'sonata-api-contacts',
            'converse-notification',
            'converse-omemo',
            'converse-signal-protocol',
            'converse-mam',
            'converse-roster',
            'converse-chat-markers',
            'sonata-sync-plugin',
            'sonata-global-contacts',
            'sonata-debug',
            'sonata-kick-handler',
            'sonata-reconnect-ui',
        ],
        provide_chat_markers: true,
        show_chat_markers: true,
        send_chat_markers: ['received', 'displayed'],
        show_chat_receipts: true,
        omemo_default: true,
        auto_become_omemo: true,
        omemo_trust: 'always',
        // Шифрування лише в приватних чатах. У групах OMEMO вимкнено:
        // інакше converse намагається зашифрувати груповe повідомлення,
        // не може отримати devicelist кімнати і повідомлення НЕ надсилається.
        allow_muc_omemo: false,
        message_archiving: 'always',
        archived_messages_page_size: 50,
        mam_request_all_pages: false,
        allow_message_snippets: true,
        prune_messages_above: 200,
        clear_cache_on_logout: false,
        lazy_load_messages: true,
        loglevel: 'error',
        // Default ('/images/logo/conversejs-filled.svg') 404s here: that path
        // has never existed at the app root (only under dist/images/logo/,
        // a build-copy destination) or in the website build. An SVG is also
        // the wrong format for a Windows toast icon anyway — use the PNG
        // logo that dist/ actually ships.
        notification_icon: 'dist/images/logo/conversejs-filled-192.png',
        // converse's own default is '/dist' (leading slash). That resolves
        // fine on the web (relative to the domain root), but under file://
        // (Electron) a leading slash resolves to the actual filesystem root
        // (e.g. C:\dist\...) instead of the app folder — breaking emoji.json,
        // custom emoji images, and anything else built from assets_path.
        // A relative path works identically under both http(s):// and file://.
        //
        // Trailing slash matters: entry.js's load() does
        // `__webpack_public_path__ = settings.assets_path` verbatim at
        // runtime, overwriting rspack.common.js's own (correctly slashed)
        // build-time publicPath. Without the trailing slash here, that
        // runtime value ("dist") gets concatenated directly onto chunk
        // filenames ("chunkjs.cjs/...") with no separator, producing
        // "distchunkjs.cjs/..." — breaking every dynamically-imported chunk,
        // including the i18n locale bundles.
        assets_path: 'dist/',
    });
}

/* =========================
   LOGIN
   ========================= */

/**
 * Runs the full login sequence against the Sonata REST API: create a token,
 * fetch subscriber info, and either start the chat or signal that a
 * password change is required.
 *
 * This used to be duplicated almost line-for-line between this function and
 * the login form's submit handler (the latter was the only one actually
 * driving the UI; this one was only reached from the password-change
 * follow-up). Unified here as the single source of truth for "what does a
 * login attempt do" — the submit handler below only owns UI state.
 *
 * @param {string} login
 * @param {string} password
 * @returns {Promise<'success'|'password-change-required'>}
 */
async function performLogin(login, password) {
    // No S-Chat-Token on /token/create (same as iOS): at login there is no
    // trustworthy rid for this session yet.
    const headers = authHeaders(getDeviceId(), { chatToken: false });

    let res, data;
    try {
        res = await fetch(`${apiRoot()}/token/create`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ login, password }),
        });
        data = await res.json();
    } catch (err) {
        throw new Error("Помилка з'єднання");
    }

    if (!res.ok || !data.access_token) {
        throw new Error(data.message || 'Помилка авторизації');
    }

    sessionStorage.setItem('temp_token', data.refresh_token);
    localStorage.setItem('temp_tokens', data.access_token);
    localStorage.setItem('s_refresh_token', data.refresh_token);

    const infoRes = await fetch(`${apiRoot()}/subscriber/info`, {
        headers: { ...headers, 'Authorization': `Bearer ${data.access_token}` },
    });

    if (infoRes.status === 423) {
        return 'password-change-required';
    }

    const info = await infoRes.json();
    if (!infoRes.ok) {
        throw new Error(info.message || 'Помилка авторизації');
    }

    const finalJid = info.jid || `${info.number}@${CURRENT_CONFIG.xmpp_host}`;
    sessionStorage.setItem('s_jid', finalJid);
    sessionStorage.setItem('s_pass', password);
    sessionStorage.setItem('s_token', data.access_token);
    sessionStorage.setItem('s_fullname', info.fullName);
    localStorage.setItem('s_login', login);
    persistCredentials(finalJid, password, login);

    console.log('✓ User authenticated:', finalJid);
    scheduleTokenRefresh(data.expires_in);
    startChat(finalJid, password, data.access_token);
    return 'success';
}

/* =========================
   QR LOGIN
   ========================= */
/**
 * A companion device (phone) scans a QR code we display, then confirms it
 * against the backend on our behalf. We never talk to the phone directly:
 * we only 1) ask for a qrToken/qrData pair, 2) render qrData as a QR image,
 * and 3) poll with qrToken until the backend reports the phone confirmed it
 * (201, one-time — the accessToken it returns doubles as the XMPP password,
 * same as a normal password login's access_token), it's still pending (200),
 * or it's dead — expired/already used/wrong device (404).
 */

let qrPollTimer = null;
let qrExpiryTimer = null;
let qrAccessToken = null;

function stopQrPolling() {
    if (qrPollTimer) clearInterval(qrPollTimer);
    if (qrExpiryTimer) clearTimeout(qrExpiryTimer);
    qrPollTimer = null;
    qrExpiryTimer = null;
}

function showAuthScreen(name) {
    ['sonata-login-form', 'sonata-qr-screen', 'sonata-qr-success', 'sonata-qr-invalid'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.style.display = id === name ? 'flex' : 'none';
    });
}

async function checkQrStatus(qrToken) {
    let res, data;
    try {
        res = await fetch(`${apiRoot()}/qr-code/web-auth/check-qr`, {
            method: 'POST',
            headers: authHeaders(getDeviceId()),
            body: JSON.stringify({ qrToken }),
        });
        data = await res.json().catch(() => null);
    } catch (e) {
        console.warn('QR status check network error, will retry:', e);
        return;
    }

    if (res.status === 404) {
        stopQrPolling();
        showAuthScreen('sonata-qr-invalid');
        return;
    }

    // Confirmed. The documented contract is a 201 with camelCase fields, but
    // dmr answers with plain 200 and the same snake_case shape /token/create
    // uses -- so the real success signal is "body carries an access token",
    // not the status code.
    const accessToken = data?.accessToken || data?.access_token;
    if (accessToken) {
        stopQrPolling();
        qrAccessToken = accessToken;
        const refreshToken = data.refreshToken || data.refresh_token;
        if (refreshToken) localStorage.setItem('s_refresh_token', refreshToken);
        scheduleTokenRefresh(data.expiresIn ?? data.expires_in);
        showAuthScreen('sonata-qr-success');
        // Auto-continue straight into the chat once the phone confirms the
        // QR code, rather than waiting on the "Продовжити" click -- the
        // success screen still flashes briefly so the user sees it worked.
        try {
            await completeQrLogin();
        } catch (err) {
            console.error('QR login completion failed:', err);
            showAuthScreen('sonata-qr-invalid');
        }
        return;
    }
    // No token yet — still pending, keep polling.
}

async function startQrLogin() {
    stopQrPolling();
    qrAccessToken = null;
    showAuthScreen('sonata-qr-screen');

    let data;
    try {
        const res = await fetch(`${apiRoot()}/qr-code/web-auth/get-token`, { headers: authHeaders(getDeviceId()) });
        data = await res.json();
        if (!res.ok || !data.qrData) throw new Error(data.message || 'Не вдалося отримати QR-код');
    } catch (e) {
        console.error('Failed to fetch QR token:', e);
        showAuthScreen('sonata-qr-invalid');
        return;
    }

    const canvas = document.getElementById('sonata-qr-canvas');
    try {
        await QRCode.toCanvas(canvas, data.qrData, { width: 220, margin: 1 });
    } catch (e) {
        console.error('QR render failed:', e);
    }

    qrPollTimer = setInterval(() => checkQrStatus(data.qrToken), 5000);
    qrExpiryTimer = setTimeout(() => {
        stopQrPolling();
        showAuthScreen('sonata-qr-invalid');
    }, data.expiresIn * 1000);
}

/**
 * Mirrors performLogin()'s post-token step (fetch subscriber info, then
 * startChat), but the QR check response has no `login` field the way a
 * typed-in username does — refreshSonataToken() silently no-ops without
 * localStorage's s_login, so this assumes /subscriber/info returns one;
 * if it doesn't, a QR-authenticated session just won't auto-refresh its
 * token and will need a fresh QR/password login once it expires.
 */
async function completeQrLogin() {
    let accessToken = qrAccessToken;
    if (!accessToken) {
        showAuthScreen('sonata-login-form');
        return;
    }

    // check-qr's access_token can arrive already expired (dmr sends
    // expires_in: 0), which makes /subscriber/info 401 immediately below.
    // Exchange it for a working one via /token/refresh first -- login is
    // optional on that endpoint (added in 1.7.1 only for server-side
    // logging), so the refresh_token alone is enough.
    const qrRefreshToken = localStorage.getItem('s_refresh_token');
    if (qrRefreshToken) {
        try {
            const refreshRes = await fetch(`${apiRoot()}/token/refresh`, {
                method: 'POST',
                headers: authHeaders(getDeviceId()),
                body: JSON.stringify({ refresh_token: qrRefreshToken }),
            });
            const refreshData = await refreshRes.json();
            if (refreshRes.ok && refreshData.access_token) {
                accessToken = refreshData.access_token;
                qrAccessToken = accessToken;
                localStorage.setItem('s_refresh_token', refreshData.refresh_token);
                scheduleTokenRefresh(refreshData.expires_in);
            }
        } catch (e) {
            console.warn('QR token refresh failed, falling back to the original token:', e);
        }
    }

    const headers = { ...authHeaders(getDeviceId()), 'Authorization': `Bearer ${accessToken}` };
    const infoRes = await fetch(`${apiRoot()}/subscriber/info`, { headers });
    const info = await infoRes.json();
    if (!infoRes.ok) {
        throw new Error(info.message || 'Помилка авторизації');
    }

    const finalJid = info.jid || `${info.number}@${CURRENT_CONFIG.xmpp_host}`;
    sessionStorage.setItem('s_jid', finalJid);
    sessionStorage.setItem('s_pass', accessToken);
    sessionStorage.setItem('s_token', accessToken);
    sessionStorage.setItem('s_fullname', info.fullName);
    if (info.login) localStorage.setItem('s_login', info.login);
    persistCredentials(finalJid, accessToken, info.login, 'oauth');

    startChat(finalJid, accessToken, accessToken, true);
}

/* =========================
   DOM WIRING
   ========================= */

updateEndpoints('sonata.dolya.ua', '011');

document.getElementById('sonata-login-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const container = document.getElementById('auth-msg-container');
    const msg = document.getElementById('auth-msg');
    const btn = document.getElementById('sonata-submit-btn');
    const pwdChangeFields = document.getElementById('password-change-fields');

    const login = document.getElementById('api-login').value;
    const pass = document.getElementById('api-pass').value;

    container.classList.add('is-loading');
    msg.textContent = '';
    btn.disabled = true;

    // Follow-up step: the server demanded a password change on the previous
    // submit, and the user has now filled in the new-password fields.
    if (pwdChangeFields && pwdChangeFields.style.display === 'block') {
        const newPass = document.getElementById('new-password').value;
        const confPass = document.getElementById('confirm-password').value;
        const tempToken = localStorage.getItem('temp_tokens');

        if (newPass !== confPass) {
            msg.textContent = 'Паролі не збігаються';
            btn.disabled = false;
            container.classList.remove('is-loading');
            return;
        }

        try {
            const cpRes = await fetch(`${apiRoot()}/subscriber/change-password-otp`, {
                method: 'POST',
                headers: { ...authHeaders(getDeviceId()), 'Authorization': `Bearer ${tempToken}` },
                body: JSON.stringify({
                    oldPassword: pass,
                    password: newPass,
                    password_confirmation: confPass,
                }),
            });

            if (!cpRes.ok) {
                let errorMessage = 'Сервер відхилив зміну пароля';
                try {
                    const cpData = await cpRes.json();
                    errorMessage = cpData.message || errorMessage;
                } catch (e) {
                    // Порожнє тіло відповіді — лишаємо дефолтне повідомлення.
                }
                msg.textContent = errorMessage;
                btn.disabled = false;
                container.classList.remove('is-loading');
                return;
            }

            sessionStorage.removeItem('temp_token');
            document.getElementById('api-pass').value = newPass;
            pwdChangeFields.style.display = 'none';
            btn.textContent = 'Увійти';
            msg.style.color = 'green';
            msg.textContent = 'Пароль змінено! Входжу...';

            const result = await performLogin(login, newPass);
            if (result === 'password-change-required') {
                // Сервер все ще вимагає зміну пароля — показуємо форму знову.
                msg.style.color = '';
                pwdChangeFields.style.display = 'block';
                btn.textContent = 'Змінити пароль та увійти';
                msg.textContent = 'Необхідно змінити пароль';
                container.classList.remove('is-loading');
                btn.disabled = false;
            }
        } catch (err) {
            msg.textContent = 'Помилка: ' + err.message;
            btn.disabled = false;
            container.classList.remove('is-loading');
        }
        return;
    }

    try {
        const result = await performLogin(login, pass);
        if (result === 'password-change-required') {
            pwdChangeFields.style.display = 'block';
            btn.textContent = 'Змінити пароль та увійти';
            msg.textContent = 'Необхідно змінити пароль';
            container.classList.remove('is-loading');
            btn.disabled = false;
        }
        // 'success': performLogin() already called startChat(), which hides
        // the auth overlay — nothing left to do here.
    } catch (err) {
        msg.textContent = err.message || 'Помилка авторизації';
        btn.disabled = false;
        container.classList.remove('is-loading');
    }
});

document.getElementById('sonata-qr-login-btn')?.addEventListener('click', (e) => {
    e.preventDefault();
    startQrLogin();
});

document.getElementById('sonata-qr-cancel-btn')?.addEventListener('click', (e) => {
    e.preventDefault();
    stopQrPolling();
    showAuthScreen('sonata-login-form');
});

document.getElementById('sonata-qr-continue-btn')?.addEventListener('click', async (e) => {
    e.preventDefault();
    try {
        await completeQrLogin();
    } catch (err) {
        console.error('QR login completion failed:', err);
        showAuthScreen('sonata-qr-invalid');
    }
});

document.getElementById('sonata-qr-retry-btn')?.addEventListener('click', (e) => {
    e.preventDefault();
    showAuthScreen('sonata-login-form');
});

window.addEventListener('load', async () => {
    let j = sessionStorage.getItem('s_jid');
    let p = sessionStorage.getItem('s_pass');
    let t = sessionStorage.getItem('s_token');
    let authType = sessionStorage.getItem('s_auth_type');

    if (!j || !p) {
        // Fresh renderer (app was fully restarted, e.g. after an OS reboot) —
        // sessionStorage is empty even though the user was logged in before.
        // Fall back to the Electron-only encrypted credential store.
        const persisted = await loadPersistedCredentials();
        if (persisted) {
            j = persisted.jid;
            p = persisted.password;
            t = null;
            authType = persisted.authType;
            sessionStorage.setItem('s_jid', j);
            sessionStorage.setItem('s_pass', p);
            // Without this, a session restored via persisted credentials never
            // has s_login set at all: the profile modal's "Логін" field renders
            // empty, and refreshSonataToken() (which requires it) silently
            // no-ops, so the access token would never refresh on such a session.
            if (persisted.login) localStorage.setItem('s_login', persisted.login);
        }
    }

    if (j && p) {
        refreshSonataToken();
        startChat(j, p, t, authType === 'oauth');
    } else {
        // #sonata-loader is visible by default now (see 05-styling.css) so
        // there's no blank white screen while this 'load' handler is still
        // waiting on the page to finish loading -- but that means it must be
        // explicitly hidden here now, or it'd sit on top of the login form
        // (higher z-index) forever.
        document.getElementById('sonata-loader').style.display = 'none';
        document.getElementById('sonata-auth-overlay').style.display = 'flex';
    }
});

// Debug helper: run `sonataDebug.dumpChatbox('1100005@chat.sonata.dolya.ua')`
// in DevTools to compare a chatbox's live in-memory attrs against what's
// actually persisted in IndexedDB — useful for tracking down counters (like
// num_unread) that look reset in-memory but don't stick, or vice versa.
// _converse is only reachable from inside a registered plugin, hence this
// tiny one instead of a bare window-global reading it directly.
converse.plugins.add('sonata-debug', {
    initialize() {
        const { _converse } = this;
        _api = _converse.api; // see the _api declaration far above for why this is needed
        _converseForChatToken = _converse; // see currentChatToken()

        // A fresh login creates the backend session WITHOUT a chatToken
        // (/token/create carries none). Tell the backend our rid as soon as
        // OMEMO knows it, via one token refresh carrying S-Chat-Token, instead
        // of waiting for the next scheduled refresh. Once per tab session per
        // rid, so a reload doesn't refresh again for nothing.
        _converse.api.listen.on('OMEMOInitialized', () => {
            const token = currentChatToken();
            if (!token) return;
            try {
                if (sessionStorage.getItem('sonata_chat_token_announced') === token) return;
                sessionStorage.setItem('sonata_chat_token_announced', token);
            } catch (_) {
                /* storage unavailable: announce anyway */
            }
            console.log(`[Sonata] Announcing S-Chat-Token ${token} to the backend (token refresh)`);
            refreshSonataToken();
        });
        // Counters for sonataDebug.connection(): how often each lifecycle
        // event has fired since the page loaded.
        const sonata_event_counts = { connected: 0, reconnected: 0, presencesInitialized: 0, since: new Date().toISOString() };
        ['connected', 'reconnected', 'presencesInitialized'].forEach((ev) =>
            _converse.api.listen.on(ev, () => {
                sonata_event_counts[ev]++;
            }),
        );
        window.sonataDebug = {
            dumpChatbox: async (jid) => {
                const chatbox = _converse.state.chatboxes.get(jid);
                if (!chatbox) {
                    console.log(`[Sonata] No chatbox in memory for jid: ${jid}`);
                    return;
                }
                console.log(`[Sonata] In-memory attrs for ${jid}:`, chatbox.attributes);
                try {
                    // browserStorage lives on the chatboxes COLLECTION, not
                    // on each individual chatbox model.
                    const storage = chatbox.browserStorage || chatbox.collection?.browserStorage;
                    const stored = await storage?.find(chatbox);
                    console.log(`[Sonata] Persisted (IndexedDB) attrs for ${jid}:`, stored);
                } catch (e) {
                    console.error(`[Sonata] Could not read persisted attrs for ${jid}:`, e);
                }
            },

            // Lists the messages of one chat that failed OMEMO decryption (or
            // carried no key for this device). They are stored but never
            // rendered, so to the user they "never arrived". Read-only.
            //   await sonataDebug.decryptErrors('1100007@chat.sonata.dolya.ua')
            decryptErrors: async (jid) => {
                const chatbox = _converse.state.chatboxes.get(jid);
                if (!chatbox) {
                    console.log(`[Sonata] No chatbox in memory for jid: ${jid}`);
                    return null;
                }
                const all = chatbox.messages?.models || [];
                const failed = all.filter((m) => m.get('is_error') && m.get('error_type') === 'Decryption');
                const rows = failed.map((m) => ({
                    time: m.get('time'),
                    msgid: m.get('msgid') || m.get('id'),
                    sender_device: m.get('encrypted')?.device_id ?? null,
                    condition: m.get('error_condition') || null,
                    message: m.get('error_message') || null,
                    archived: !!m.get('is_archived'),
                }));
                rows.sort((a, b) => String(a.time).localeCompare(String(b.time)));
                const by_condition = {};
                rows.forEach((r) => {
                    const k = `${r.condition || '?'} | device ${r.sender_device ?? '?'}`;
                    by_condition[k] = (by_condition[k] || 0) + 1;
                });
                const report = {
                    chat: jid,
                    messages_in_memory: all.length,
                    decrypt_failures: rows.length,
                    by_condition,
                    failures: rows.slice(-50),
                };
                console.log('[Sonata] decrypt errors report:\n' + JSON.stringify(report, null, 2));
                return report;
            },

            // The newest N messages this client holds for a chat, INCLUDING the
            // ones that are stored but hidden (decrypt failures, key-only
            // messages, errors). Read-only. Use it to check whether a message
            // the server has (see mamRaw) is present locally and why it is or
            // is not shown.
            //   sonataDebug.lastMessages('1100007@chat.sonata.dolya.ua', 25)
            lastMessages: (jid, n = 25) => {
                const chatbox = _converse.state.chatboxes.get(jid);
                if (!chatbox) {
                    console.log(`[Sonata] No chatbox in memory for jid: ${jid}`);
                    return null;
                }
                const models = [...(chatbox.messages?.models || [])];
                models.sort((a, b) => String(a.get('time')).localeCompare(String(b.get('time'))));
                const rows = models.slice(-n).map((m) => {
                    const stanza_ids = {};
                    Object.keys(m.attributes)
                        .filter((k) => k.startsWith('stanza_id'))
                        .forEach((k) => (stanza_ids[k] = m.get(k)));
                    return {
                        time: m.get('time'),
                        msgid: m.get('msgid'),
                        from: m.get('from'),
                        sender: m.get('sender'),
                        type: m.get('type'),
                        shown_as_text: !!(m.get('body') || m.get('message')),
                        is_error: !!m.get('is_error'),
                        error: m.get('error_condition') || null,
                        error_message: m.get('error_message') || null,
                        is_only_key: !!m.get('is_only_key'),
                        is_ephemeral: !!m.get('is_ephemeral'),
                        is_archived: !!m.get('is_archived'),
                        encrypted_from_device: m.get('encrypted')?.device_id ?? null,
                        stanza_ids,
                    };
                });
                console.log('[Sonata] last messages:\n' + JSON.stringify({ chat: jid, total_in_memory: models.length, rows }, null, 2));
                console.table(
                    rows.map((r) => ({ time: r.time, msgid: r.msgid, from: r.from, text: r.shown_as_text, err: r.error, only_key: r.is_only_key, dev: r.encrypted_from_device })),
                );
                return rows;
            },

            // Scans EVERY one-to-one chat for the last N hours (default 48) and
            // reports, per chat, how many encrypted messages were not encrypted
            // for this device, how many had a key but are missing here, and
            // which kind of client sent the keyless ones. One command, no JIDs:
            //   await sonataDebug.scanAll()
            //   await sonataDebug.scanAll({ hours: 72 })
            // Read-only; the result is also copied to the clipboard.
            scanAll: async (opts = {}) => {
                const d = window.sonataDebug;
                const hours = opts.hours || 48;
                const end = new Date();
                const start = new Date(end.getTime() - hours * 3600 * 1000);
                const quiet = async (f) => {
                    const l = console.log;
                    const t = console.table;
                    console.log = () => {};
                    console.table = () => {};
                    try {
                        return await f();
                    } catch (e) {
                        return { failed: String(e) };
                    } finally {
                        console.log = l;
                        console.table = t;
                    }
                };
                const kind = (from) => {
                    const res = (from || '').split('/')[1] || '';
                    if (res.startsWith('Sonata-iOS')) return 'iOS';
                    if (res.startsWith('Sonata[')) return 'Android ' + res.slice(6, res.indexOf(']'));
                    if (res.startsWith('Web-')) return 'Web/Desktop';
                    return res.slice(0, 12) || 'unknown';
                };
                const chats = (_converse.state.chatboxes.models || [])
                    .filter((c) => c.get('type') === 'chatbox')
                    .slice(0, 80);
                const report = { hours, scanned: 0, chats: [] };
                for (const chat of chats) {
                    const jid = chat.get('jid');
                    const a = await quiet(() => d.mamRaw(jid, { start: start.toISOString(), end: end.toISOString(), max: 300 }));
                    if (!a || a.failed) continue;
                    report.scanned += 1;
                    const local = await quiet(() => d.lastMessages(jid, 600));
                    const have = new Set((Array.isArray(local) ? local : []).map((x) => x.msgid));
                    const rows = a.stanzas.filter(
                        (s) => s.encrypted && s.has_payload && String(s.sender_device) !== String(a.this_device),
                    );
                    if (!rows.length) continue;
                    const no_key = rows.filter((r) => r.has_key_for_this_device === false);
                    const lost = rows.filter((r) => r.has_key_for_this_device && !have.has(r.msg_id));
                    const by_client = {};
                    no_key.forEach((r) => {
                        const k = kind(r.from);
                        by_client[k] = (by_client[k] || 0) + 1;
                    });
                    report.chats.push({
                        chat: jid,
                        name: chat.get('fullname') || chat.get('nickname') || '',
                        encrypted_messages: rows.length,
                        no_key_for_this_device: no_key.length,
                        key_but_missing_here: lost.length,
                        no_key_by_client: by_client,
                        first_no_key: no_key[0] ? no_key[0].time : null,
                        last_no_key: no_key.length ? no_key[no_key.length - 1].time : null,
                    });
                }
                report.chats.sort(
                    (x, y) => y.no_key_for_this_device + y.key_but_missing_here - (x.no_key_for_this_device + x.key_but_missing_here),
                );
                report.totals = {
                    no_key: report.chats.reduce((n, c) => n + c.no_key_for_this_device, 0),
                    key_but_missing_here: report.chats.reduce((n, c) => n + c.key_but_missing_here, 0),
                };
                console.table(
                    report.chats.map((c) => ({
                        chat: c.name || c.chat,
                        total: c.encrypted_messages,
                        no_key: c.no_key_for_this_device,
                        lost_here: c.key_but_missing_here,
                        clients_without_key: JSON.stringify(c.no_key_by_client),
                        last_no_key: (c.last_no_key || '').slice(0, 16),
                    })),
                );
                console.log(report.totals);
                const text = JSON.stringify(report);
                console.log('===== scanAll summary (copied to clipboard when allowed) =====');
                console.log(text);
                try {
                    if (typeof copy === 'function') copy(text);
                } catch (_) {
                    /* copy() exists only in the DevTools console */
                }
                return report;
            },

            // Forces a re-read of the last N hours (default 36) of every
            // one-to-one chat from the server archive, ignoring the catch-up
            // anchor, then reports how many messages each chat gained. This is
            // also what runs automatically after start and after a reconnect.
            //   await sonataDebug.resyncHistory()
            //   await sonataDebug.resyncHistory({ hours: 72 })
            resyncHistory: async (opts = {}) => {
                const { u } = converse.env;
                const hours = opts.hours || 36;
                const chats = (_converse.state.chatboxes.models || []).filter((c) => c.get('type') === 'chatbox');
                const before = {};
                chats.forEach((c) => (before[c.get('jid')] = c.messages?.length || 0));
                u.mam.deepResyncKnownChats(hours, true);
                await new Promise((r) => setTimeout(r, opts.wait || 30000));
                const gained = chats
                    .map((c) => ({
                        chat: c.get('fullname') || c.get('jid'),
                        jid: c.get('jid'),
                        new_messages: (c.messages?.length || 0) - before[c.get('jid')],
                    }))
                    .filter((x) => x.new_messages > 0);
                const report = { hours, chats: chats.length, chats_with_new_messages: gained.length, gained };
                console.table(gained);
                console.log(JSON.stringify(report));
                try {
                    if (typeof copy === 'function') copy(JSON.stringify(report));
                } catch (_) {
                    /* copy() exists only in the DevTools console */
                }
                return report;
            },

            // One-command full check, so nothing has to be pasted:
            //   await sonataDebug.fullCheck()
            //   await sonataDebug.fullCheck({ pronenko: '1100048@chat.sonata.dolya.ua' })
            // Runs omemo(), then for the chat with Evhen and with Pronenko compares
            // what the SERVER archive holds for the morning of 2026-10-15 with what
            // this client stored, and prints a summary (also copied to the clipboard).
            // Read-only.
            fullCheck: async (opts = {}) => {
                const d = window.sonataDebug;
                const EVHEN = opts.evhen || '1169266@chat.sonata.dolya.ua';
                const candidates = opts.pronenko
                    ? [opts.pronenko]
                    : ['11048@chat.sonata.dolya.ua', '1100048@chat.sonata.dolya.ua'];
                const WIN = {
                    start: opts.start || '2026-10-15T05:00:00Z',
                    end: opts.end || '2026-10-15T09:00:00Z',
                    max: 300,
                };
                const quiet = async (f) => {
                    const l = console.log;
                    const t = console.table;
                    console.log = () => {};
                    console.table = () => {};
                    try {
                        return await f();
                    } catch (e) {
                        return { failed: String(e) };
                    } finally {
                        console.log = l;
                        console.table = t;
                    }
                };
                const report = { started: new Date().toISOString() };

                const om = await quiet(() => d.omemo(EVHEN));
                console.log('===== 1. OMEMO =====');
                if (om && !om.failed) {
                    report.omemo = {
                        own_device_id: om.own_device_id,
                        own_listed_on_server: om.own_device_listed_on_server,
                        server_own_devicelist: om.server_own_devicelist,
                        server_contact_devicelist: om.server_contact_devicelist,
                        local_contact_devices: (om.local_contact_devices || []).map((x) => `${x.id}:active=${x.active}`),
                        sessions: (om.sessions_with_contact || []).length,
                    };
                    console.log(report.omemo);
                    console.table(
                        Object.entries(om.contact_bundles || {}).map(([id, b]) => ({
                            device: id,
                            prekeys_on_server: b.prekeys_on_server,
                            identity_matches: b.cached_identity_matches_server,
                            signed_prekey_matches: b.cached_signed_prekey_matches_server,
                        })),
                    );
                } else {
                    report.omemo = om;
                    console.log('omemo() failed:', om);
                }

                let PRON = null;
                for (const c of candidates) {
                    const q = await quiet(() => d.mamRaw(c, { start: WIN.start, end: WIN.end, max: 5 }));
                    if (q && !q.failed && q.stanzas_returned > 0) {
                        PRON = c;
                        break;
                    }
                }
                report.pronenko_jid = PRON;
                console.log('Pronenko JID:', PRON || 'NOT FOUND (empty archive for every candidate)');

                const checkChat = async (label, jid) => {
                    console.log(`===== ${label}: ${jid} =====`);
                    const a = await quiet(() => d.mamRaw(jid, WIN));
                    if (!a || a.failed) {
                        console.log('mamRaw failed:', a);
                        return { error: a };
                    }
                    const local = await quiet(() => d.lastMessages(jid, 400));
                    const have = new Set((Array.isArray(local) ? local : []).map((x) => x.msgid));
                    const rows = a.stanzas
                        .filter((s) => s.encrypted && s.has_payload)
                        .map((s) => ({
                            t: (s.time || '').slice(11, 19),
                            from: s.from.split('/')[1] || s.from,
                            dev: s.sender_device,
                            key_for_this_device: s.has_key_for_this_device,
                            present_locally: have.has(s.msg_id),
                            msg_id: s.msg_id,
                            rids: s.key_for_rids.join(' '),
                        }));
                    console.table(rows);
                    const summary = {
                        this_device: a.this_device,
                        encrypted_messages: rows.length,
                        archive_complete: a.complete,
                        no_key_for_this_device: rows.filter((r) => r.key_for_this_device === false).length,
                        key_but_missing_locally: rows.filter((r) => r.key_for_this_device && !r.present_locally).length,
                        ok: rows.filter((r) => r.key_for_this_device && r.present_locally).length,
                        missing_rows: rows
                            .filter((r) => !r.present_locally)
                            .map((r) => `${r.t} dev=${r.dev} key=${r.key_for_this_device}`),
                        missing_ids: rows.filter((r) => !r.present_locally).map((r) => r.msg_id),
                    };
                    console.log(summary);
                    return summary;
                };

                report.chat_evhen = await checkChat('2. Chat with Evhen', EVHEN);
                if (PRON) report.chat_pronenko = await checkChat('3. Chat with Pronenko', PRON);

                console.log('===== 4. Device list history =====');
                try {
                    d.deviceListHistory({ jid: EVHEN, n: 40 });
                } catch (e) {
                    console.log(e);
                }

                try {
                    const trace = JSON.parse(localStorage.getItem('sonata_message_trace') || '[]');
                    const miss = [];
                    [report.chat_evhen, report.chat_pronenko].forEach((c) => {
                        ((c && c.missing_ids) || []).forEach((id) => miss.push(id));
                    });
                    report.trace_for_missing = trace
                        .filter((e) => miss.includes(e.id))
                        .map((e) => ({ t: (e.t || '').slice(11, 19), kind: e.kind, outcome: e.outcome || (e.ok ? 'decrypted' : 'DECRYPT FAILED'), err: e.err || null }));
                } catch (_) {
                    /* trace unavailable */
                }
                const text = JSON.stringify(report);
                console.log('===== SUMMARY (copied to clipboard when allowed) =====');
                console.log(text);
                try {
                    if (typeof copy === 'function') copy(text);
                } catch (_) {
                    /* copy() exists only in the DevTools console */
                }
                return report;
            },

            // What happened to each incoming message (persistent, survives
            // reloads): 'decrypt' rows (ok / error) and 'chat' rows (created,
            // duplicate, marker, receipt, retraction, not-created-empty,
            // parse-error). Use it when a message the server holds never shows up.
            //   sonataDebug.messageTrace({ n: 60 })
            //   sonataDebug.messageTrace({ id: 'af045318-3518-423e-bdd6-5c0b61da5eef' })
            messageTrace: (opts = {}) => {
                let list = [];
                try {
                    list = JSON.parse(localStorage.getItem('sonata_message_trace') || '[]');
                } catch (_) {
                    list = [];
                }
                if (opts.id) list = list.filter((e) => e.id === opts.id);
                const rows = list.slice(-(opts.n || 60));
                console.table(
                    rows.map((e) => ({
                        time: (e.t || '').slice(5, 19).replace('T', ' ') + 'Z',
                        kind: e.kind,
                        outcome: e.outcome || (e.ok ? 'decrypted' : 'DECRYPT FAILED'),
                        id: e.id,
                        from: (e.from || '').split('/')[1] || e.from,
                        device: e.device || '',
                        archived: e.archived,
                        err: e.err || '',
                    })),
                );
                return rows;
            },

            // Persistent log (survives reloads) of every OMEMO device list this
            // client received a push for ('push') or published itself
            // ('publish', with a short caller trace). Built to find out WHO
            // rewrites a device list and when. Newest last.
            //   sonataDebug.deviceListHistory()                                  // everything (last 60)
            //   sonataDebug.deviceListHistory({ jid: '1100007@chat.sonata.dolya.ua', n: 100 })
            deviceListHistory: (opts = {}) => {
                let list = [];
                try {
                    list = JSON.parse(localStorage.getItem('sonata_devicelist_history') || '[]');
                } catch (_) {
                    list = [];
                }
                if (opts.jid) {
                    const bare = opts.jid;
                    list = list.filter((e) => e.kind === 'publish' || String(e.jid || '').startsWith(bare));
                }
                const rows = list.slice(-(opts.n || 60)).map((e) => ({
                    time: e.t.slice(5, 19).replace('T', ' ') + 'Z',
                    kind: e.kind,
                    jid: e.jid || '(own publish)',
                    ids: (e.ids || []).join(' '),
                    retract: e.retract || '',
                    purge: e.purge || '',
                    caller: (e.caller || []).slice(0, 2).join(' < '),
                }));
                console.table(rows);
                return list.slice(-(opts.n || 60));
            },

            // Re-reads ONE small time window of a chat's server archive through
            // the normal import path (parse -> decrypt -> store), so a message
            // the server holds but this client lost is imported again if it can
            // be decrypted, and any failure is logged with its reason. Messages
            // already held locally are skipped as duplicates. Keep the window
            // small (minutes). start/end are UTC ISO strings.
            //   await sonataDebug.mamRecover('1100007@chat.sonata.dolya.ua', { start: '2026-10-15T07:30:00Z', end: '2026-10-15T07:31:30Z' })
            mamRecover: async (jid, opts = {}) => {
                const { u } = converse.env;
                const chatbox = _converse.state.chatboxes.get(jid);
                if (!chatbox) {
                    console.log(`[Sonata] No chatbox in memory for jid: ${jid}`);
                    return null;
                }
                if (!opts.start || !opts.end) {
                    console.log('[Sonata] mamRecover needs { start, end } (UTC ISO strings)');
                    return null;
                }
                const ids = () => new Set((chatbox.messages?.models || []).map((m) => m.get('msgid')));
                const before = ids();
                try {
                    await u.mam.fetchArchivedMessages(chatbox, { mam: { start: opts.start, end: opts.end }, rsm: { max: 100 } }, false);
                } catch (e) {
                    console.error('[Sonata] mamRecover: fetch failed:', e);
                }
                // queueMessage() processes asynchronously; give it time to finish.
                await new Promise((r) => setTimeout(r, 3000));
                const added = (chatbox.messages?.models || [])
                    .filter((m) => !before.has(m.get('msgid')))
                    .map((m) => ({
                        time: m.get('time'),
                        msgid: m.get('msgid'),
                        from: m.get('from'),
                        shown_as_text: !!(m.get('body') || m.get('message')),
                        is_error: !!m.get('is_error'),
                        error: m.get('error_condition') || null,
                        error_message: m.get('error_message') || null,
                        is_only_key: !!m.get('is_only_key'),
                        encrypted_from_device: m.get('encrypted')?.device_id ?? null,
                    }));
                const report = { chat: jid, window: opts, newly_stored: added.length, messages: added };
                console.log('[Sonata] mamRecover report:\n' + JSON.stringify(report, null, 2));
                return report;
            },

            // Reads the SERVER archive (MAM) for one chat and one time window
            // and summarises every stored stanza WITHOUT processing it, so it
            // shows what the server really holds, independent of what this
            // client displays. Answers "was the message ever stored, and for
            // which OMEMO devices was it encrypted?".
            //   await sonataDebug.mamRaw('1100007@chat.sonata.dolya.ua', { start: '2026-10-15T05:00:00Z', end: '2026-10-15T09:00:00Z' })
            // start/end are UTC ISO strings (local time minus the UTC offset).
            mamRaw: async (jid, opts = {}) => {
                const { sizzle, Strophe } = converse.env;
                const store = _converse.state.omemo_store;
                const my_device = store ? String(store.get('device_id')) : null;
                const mam = { with: jid };
                if (opts.start) mam.start = opts.start;
                if (opts.end) mam.end = opts.end;
                let result;
                try {
                    result = await _converse.api.archive.query({ mam, rsm: { max: opts.max || 100 } });
                } catch (e) {
                    console.error('[Sonata] mamRaw query failed:', e);
                    return null;
                }
                const rows = (result.messages || []).map((wrapper) => {
                    const inner = sizzle('forwarded > message', wrapper).pop() || wrapper;
                    const enc = sizzle(`encrypted[xmlns="${Strophe.NS.OMEMO}"]`, inner).pop();
                    const rids = enc ? sizzle('header > key', enc).map((k) => k.getAttribute('rid')) : [];
                    const stamp = sizzle('forwarded > delay', wrapper).pop()?.getAttribute('stamp') || null;
                    return {
                        archive_id: sizzle('result', wrapper).pop()?.getAttribute('id') || wrapper.getAttribute('id'),
                        time: stamp,
                        from: inner.getAttribute('from'),
                        msg_id: inner.getAttribute('id'),
                        encrypted: !!enc,
                        sender_device: enc?.querySelector('header')?.getAttribute('sid') ?? null,
                        key_for_rids: rids,
                        has_key_for_this_device: my_device ? rids.includes(my_device) : null,
                        has_payload: !!enc?.querySelector('payload'),
                        has_plain_body: !!sizzle('body', inner).length,
                        is_correction: !!sizzle('replace[xmlns="urn:xmpp:message-correct:0"]', inner).length,
                        is_marker: !!sizzle('[xmlns="urn:xmpp:chat-markers:0"]', inner).length,
                    };
                });
                const report = {
                    chat: jid,
                    this_device: my_device,
                    window: { start: opts.start || null, end: opts.end || null },
                    stanzas_returned: rows.length,
                    complete: result.complete ?? null,
                    stanzas: rows,
                };
                console.log('[Sonata] MAM raw report:\n' + JSON.stringify(report, null, 2));
                return report;
            },

            // Connection diagnostics. Run in DevTools:  sonataDebug.connection()
            // Answers "is every incoming stanza handled more than once?":
            // lists the Strophe stanza handlers currently registered and flags
            // identical ones (a handler registered twice processes -- and
            // notifies for -- every stanza twice), plus XEP-0198 stream
            // management counters and how many times connect / reconnect /
            // presencesInitialized have fired since the page loaded.
            connection: () => {
                const conn = _converse.api.connection.get();
                const handlers = (conn?.handlers || []).map((h) => ({
                    ns: h.ns ?? null,
                    name: h.name ?? null,
                    type: h.type ?? null,
                    id: h.id ?? null,
                    from: h.from ?? null,
                    fn: String(h.handler).replace(/\s+/g, ' ').slice(0, 70),
                }));
                const counts = {};
                handlers.forEach((h) => {
                    const k = JSON.stringify(h);
                    counts[k] = (counts[k] || 0) + 1;
                });
                const duplicates = Object.entries(counts)
                    .filter(([, n]) => n > 1)
                    .map(([k, n]) => ({ times: n, handler: JSON.parse(k) }));
                const s = _converse.session;
                const report = {
                    handlers_total: handlers.length,
                    message_handlers: handlers.filter((h) => h.name === 'message').length,
                    duplicate_handlers: duplicates,
                    smacks: {
                        enabled: s?.get('smacks_enabled'),
                        has_stream_id: !!s?.get('smacks_stream_id'),
                        num_stanzas_handled: s?.get('num_stanzas_handled'),
                        num_stanzas_handled_by_server: s?.get('num_stanzas_handled_by_server'),
                        unacked_stanzas: s?.get('unacked_stanzas')?.length,
                    },
                    event_counts: { ...sonata_event_counts },
                };
                console.log('[Sonata] connection report:\n' + JSON.stringify(report, null, 2));
                return report;
            },

            // OMEMO pair diagnostics. Run in DevTools:
            //   await sonataDebug.omemo('1100017@chat.sonata.dolya.ua')
            // Read-only: asks the SERVER (not the local cache) for our own and
            // the contact's device lists and bundles and compares them with
            // what this tab has cached/sessioned. Built to tell apart "the
            // other side doesn't know/trust our device" (their cache or trust
            // setting) from "our cache/session is stale" when a single
            // contact can't read us or we can't read them.
            omemo: async (jid) => {
                const { stx, sizzle, Strophe, u } = converse.env;
                const store = _converse.state.omemo_store;
                const bare = _converse.session.get('bare_jid');
                if (!store) {
                    console.warn('[Sonata] OMEMO is not initialized yet');
                    return null;
                }
                const iqError = (e) => {
                    try {
                        return 'error:' + (e?.querySelector?.('error')?.firstElementChild?.nodeName || String(e));
                    } catch (_) {
                        return 'error:' + String(e);
                    }
                };
                const serverDeviceIds = async (target) => {
                    try {
                        const iq = await _converse.api.sendIQ(stx`
                            <iq type="get" from="${bare}" to="${target}" xmlns="jabber:client">
                                <pubsub xmlns="${Strophe.NS.PUBSUB}"><items node="${Strophe.NS.OMEMO_DEVICELIST}"/></pubsub>
                            </iq>`);
                        return sizzle(`list[xmlns="${Strophe.NS.OMEMO}"] device`, iq).map((d) => d.getAttribute('id'));
                    } catch (e) {
                        return iqError(e);
                    }
                };
                const serverBundle = async (target, id) => {
                    try {
                        const iq = await _converse.api.sendIQ(stx`
                            <iq type="get" from="${bare}" to="${target}" xmlns="jabber:client">
                                <pubsub xmlns="${Strophe.NS.PUBSUB}"><items node="${Strophe.NS.OMEMO_BUNDLES}:${id}"/></pubsub>
                            </iq>`);
                        const el = sizzle(`bundle[xmlns="${Strophe.NS.OMEMO}"]`, iq).pop();
                        return el ? u.omemo.parseBundle(el) : 'no-bundle-published';
                    } catch (e) {
                        return iqError(e);
                    }
                };
                const summarize = (b) =>
                    typeof b === 'string'
                        ? { result: b }
                        : {
                              prekeys_on_server: b.prekeys?.length,
                              signed_prekey_id: b.signed_prekey?.id,
                              identity_key_head: b.identity_key?.slice(0, 12),
                          };

                const own_id = String(store.get('device_id'));
                const report = { me: bare, own_device_id: own_id, contact: jid };

                report.server_own_devicelist = await serverDeviceIds(bare);
                report.own_device_listed_on_server =
                    Array.isArray(report.server_own_devicelist) && report.server_own_devicelist.includes(own_id);
                report.own_bundle_on_server = summarize(await serverBundle(bare, own_id));

                report.server_contact_devicelist = await serverDeviceIds(jid);
                const dl = await _converse.api.omemo.devicelists.get(jid);
                report.local_contact_devices = dl
                    ? dl.devices.map((d) => ({
                          id: d.get('id'),
                          active: d.get('active'),
                          trusted: d.get('trusted'),
                          has_cached_bundle: !!d.get('bundle'),
                      }))
                    : null;
                report.sessions_with_contact = Object.keys(store.attributes).filter((k) =>
                    k.startsWith('session' + jid),
                );

                report.contact_bundles = {};
                if (Array.isArray(report.server_contact_devicelist)) {
                    for (const id of report.server_contact_devicelist) {
                        const fresh = await serverBundle(jid, id);
                        const entry = summarize(fresh);
                        const cached = dl?.devices.get(id)?.get('bundle');
                        if (cached && typeof fresh !== 'string') {
                            entry.cached_prekeys = cached.prekeys?.length;
                            entry.cached_prekeys_still_on_server = (cached.prekeys || []).filter((p) =>
                                (fresh.prekeys || []).some((q) => q.id === p.id),
                            ).length;
                            entry.cached_identity_matches_server = cached.identity_key === fresh.identity_key;
                            entry.cached_signed_prekey_matches_server = cached.signed_prekey?.id === fresh.signed_prekey?.id;
                        } else if (!cached) {
                            entry.cached = 'none';
                        }
                        report.contact_bundles[id] = entry;
                    }
                }
                console.log('[Sonata] OMEMO report:\n' + JSON.stringify(report, null, 2));
                return report;
            },

            // Repairs ONE contact pair from this tab's side:
            //   await sonataDebug.omemoRepair('1100017@chat.sonata.dolya.ua')
            // 1) drops our Signal sessions with that contact, 2) re-syncs the
            // contact's device list + FRESH bundles from the server (the next
            // message builds a brand-new session), 3) re-syncs OUR OWN device
            // list from the server (never publishing the stale local cache),
            // then re-announces our device + bundle so the contact's client is
            // notified. Only touches this tab's state plus our own PEP nodes.
            omemoRepair: async (jid) => {
                const { stx, sizzle, Strophe } = converse.env;
                const store = _converse.state.omemo_store;
                const bare = _converse.session.get('bare_jid');
                if (!store) {
                    console.warn('[Sonata] OMEMO is not initialized yet');
                    return null;
                }
                const ids = async (target) => {
                    const iq = await _converse.api.sendIQ(stx`
                        <iq type="get" from="${bare}" to="${target}" xmlns="jabber:client">
                            <pubsub xmlns="${Strophe.NS.PUBSUB}"><items node="${Strophe.NS.OMEMO_DEVICELIST}"/></pubsub>
                        </iq>`);
                    return sizzle(`list[xmlns="${Strophe.NS.OMEMO}"] device`, iq).map((d) => d.getAttribute('id'));
                };
                const own_id = String(store.get('device_id'));
                const summary = { contact: jid };

                await store.removeAllSessions(jid);
                summary.sessions_removed = true;

                const contact_ids = await ids(jid);
                const dl = await _converse.api.omemo.devicelists.get(jid, true);
                for (const d of dl.devices.models.slice()) {
                    if (!contact_ids.includes(String(d.get('id')))) d.save('active', false);
                }
                for (const id of contact_ids) {
                    const device = dl.devices.get(id) || (await dl.devices.create({ id, jid }, { promise: true }));
                    device.save('active', true);
                    await device.getBundle(true);
                }
                summary.contact_devices_synced = contact_ids;

                const own_dl = await _converse.api.omemo.devicelists.get(bare, true);
                const server_own_ids = await ids(bare);
                for (const d of own_dl.devices.models.slice()) {
                    const id = String(d.get('id'));
                    if (id !== own_id && !server_own_ids.includes(id)) d.save('active', false);
                }
                for (const id of server_own_ids) {
                    const device = own_dl.devices.get(id) || (await own_dl.devices.create({ id, jid: bare }, { promise: true }));
                    device.save('active', true);
                }
                own_dl._last_published_at = 0; // bypass the 5s identical-republish guard: this one is deliberate
                await own_dl.publishDevices();
                await store.publishBundle();
                summary.own_devicelist_published = own_dl.devices.filter((d) => d.get('active')).map((d) => d.get('id'));

                console.log('[Sonata] OMEMO repair done:\n' + JSON.stringify(summary, null, 2));
                return summary;
            },

            // Prunes dead entries from OUR OWN published OMEMO device list.
            //   await sonataDebug.omemoPrune()                         // dry run: prints, changes nothing
            //   await sonataDebug.omemoPrune({ keep: ['123', '456'] }) // dry run with extra ids to keep
            //   await sonataDebug.omemoPrune({ confirm: true })        // really publish
            //   await sonataDebug.omemoPrune({ confirm: true, keep: [...] })
            // Publishes the list as {this tab's device} + `keep`. Every other
            // client that is still alive sees its own id missing from the pushed
            // list and re-adds itself (that's how OMEMO device lists self-heal;
            // our own updateDevicesFromStanza -> publishCurrentDevice does it
            // too, and its local state is first set to exactly the pushed
            // list, so it can't re-publish a stale superset). Dead devices
            // never come back. A live client that is OFFLINE meanwhile is
            // missing from the list until it reconnects, so messages sent to
            // this account in that window can't be decrypted by it.
            omemoPrune: async (opts = {}) => {
                const { stx, sizzle, Strophe } = converse.env;
                const store = _converse.state.omemo_store;
                const bare = _converse.session.get('bare_jid');
                if (!store) {
                    console.warn('[Sonata] OMEMO is not initialized yet');
                    return null;
                }
                const fetchIds = async () => {
                    const iq = await _converse.api.sendIQ(stx`
                        <iq type="get" from="${bare}" to="${bare}" xmlns="jabber:client">
                            <pubsub xmlns="${Strophe.NS.PUBSUB}"><items node="${Strophe.NS.OMEMO_DEVICELIST}"/></pubsub>
                        </iq>`);
                    return sizzle(`list[xmlns="${Strophe.NS.OMEMO}"] device`, iq).map((d) => d.getAttribute('id'));
                };
                const own_id = String(store.get('device_id'));
                const keep = new Set([own_id, ...(opts.keep || []).map(String)]);
                const before = await fetchIds();
                const would_remove = before.filter((id) => !keep.has(id));
                // Optional safety cap: refuse (even in a dry run) if the list we
                // would publish is longer than `max`, so a typo can't silently
                // keep more than intended. This is a one-off manual guard, not
                // something clients enforce -- an automatic cap would make live
                // clients trim each other's entries and re-add themselves in a
                // publish loop.
                const kept_count = before.filter((id) => keep.has(id)).length;
                if (typeof opts.max === 'number' && kept_count > opts.max) {
                    const msg = `[Sonata] OMEMO prune refused: would keep ${kept_count} devices (${[...keep].join(', ')}), max is ${opts.max}. Trim your keep list.`;
                    console.warn(msg);
                    return { refused: true, kept_count, max: opts.max };
                }
                const result = {
                    account: bare,
                    this_tab_device_id: own_id,
                    server_devicelist_before: before,
                    keep: before.filter((id) => keep.has(id)),
                    would_remove,
                    applied: false,
                };
                if (!opts.confirm) {
                    console.log(
                        '[Sonata] OMEMO prune DRY RUN -- nothing changed. Re-run with { confirm: true } to publish.\n' +
                            JSON.stringify(result, null, 2),
                    );
                    return result;
                }
                if (!would_remove.length) {
                    console.log('[Sonata] OMEMO prune: nothing to remove.');
                    return result;
                }

                const own_dl = await _converse.api.omemo.devicelists.get(bare, true);
                await own_dl.getOwnDeviceId(); // makes sure our own record exists locally
                for (const d of own_dl.devices.models.slice()) {
                    const id = String(d.get('id'));
                    d.save('active', id === own_id || (keep.has(id) && before.includes(id)));
                }
                own_dl._last_published_at = 0; // deliberate publish: bypass the 5s identical-republish guard
                await own_dl.publishDevices();

                result.server_devicelist_after = await fetchIds();
                result.applied = true;
                console.log('[Sonata] OMEMO prune applied:\n' + JSON.stringify(result, null, 2));
                return result;
            },
        };
    },
});

// SM-560 ("Підключені пристрої"): when an admin / the user's other device
// terminates this session through the backend, ejabberd closes the stream
// with <stream:error><conflict/><text>Kick from admin API</text></stream:error>.
// Strophe reports that as CONNFAIL with condition 'conflict' (the text is
// only logged); headless/shared/connection/index.js now treats 'conflict'
// as a terminal disconnection instead of auto-reconnecting (which would
// have re-logged in with the stored credentials and silently undone the
// kick). finishDisconnection() stores the condition as the connfeedback
// message right before it fires 'disconnected', so that's where we read it.
// Sonata resources are random per session, so a 'conflict' can never be an
// accidental same-resource clash here -- it's always a deliberate kick or
// replacement, and ending up on the login page is right for both.
converse.plugins.add('sonata-kick-handler', {
    initialize() {
        // Only `_converse` is injected into plugins here (pluggable.js
        // Object.assign(plugin, properties)); `this.api` is undefined and
        // crashed plugin initialization in v12.0.136 -- which aborted every
        // plugin after this one, i.e. the whole app. Go through _converse.
        const { _converse } = this;
        const api = _converse.api;
        api.listen.on('disconnected', () => {
            const reason = _converse.state.connfeedback?.get('message');
            if (reason !== 'conflict') return;
            console.warn('[Sonata] Session terminated by server (stream conflict / admin kick) -- logging out');
            forceLogout();
        });
    },
});

// Reuses the same full-screen loader shown at boot (spinner + message,
// see #sonata-loader / finishLoading() in index.html) for reconnects too.
// Without this, a dropped connection left whatever converse's own
// per-component "not connected" states happened to render underneath --
// mostly blank, since the chat UI itself hides -- which looked like an
// empty screen with a single stray dot rather than an actual loading state.
converse.plugins.add('sonata-reconnect-ui', {
    initialize() {
        // Plugins only receive `{ _converse }` (headless/utils/init.js passes
        // exactly that to pluggable), so `this.api` is undefined -- reading it
        // here crashed plugin initialization. This plugin was previously not
        // whitelisted, which is why that never surfaced.
        const api = this._converse.api;
        const { Strophe } = converse.env;
        let hide_timer = null;
        // Offline->online tracker. The XMPP `reconnected` API event that MAM
        // catch-up hangs off frequently never fires in Electron after a sleep /
        // VPN switch / network change (the websocket looks open but is dead, so
        // converse resumes silently without emitting it). connfeedback CONNECTED,
        // on the other hand, fires reliably here -- it is what hides the overlay.
        // So track the down->up edge and force the catch-up on recovery. The
        // catch-up it calls is side-effect-free: archived re-reads send no
        // markers/receipts (no notification storm) and never re-decrypt an
        // already-handled stanza (no session desync) -- see the v178/v181 fixes.
        let was_down = false;
        let last_recovery_catchup = 0;
        // Its own overlay scoped to the main chat pane (see 05-styling.css),
        // not the full-screen #sonata-loader -- reusing that one covered the
        // sidebar too, making the whole contact/chat list unusable for the
        // entire reconnect window even though it has nothing to do with the
        // connection dropping.
        const overlay = document.getElementById('sonata-reconnect-overlay');
        const overlayText = document.getElementById('reconnect-loader-text');
        const chatEl = document.getElementById('conversejs');
        if (!overlay) return;

        api.listen.on('connfeedback', (feedback) => {
            // Only react once the app has already finished its initial boot
            // (converse-ready). During first boot, startChat()/finishLoading()
            // own #sonata-loader; reacting to the same raw connection status
            // here too could hide it before OMEMO/plugins are actually ready
            // -- the exact white-screen bug it exists to avoid.
            if (!chatEl?.classList.contains('converse-ready')) return;

            const status = feedback.get('connection_status');
            const is_reconnecting = [
                Strophe.Status.RECONNECTING,
                Strophe.Status.CONNECTING,
                Strophe.Status.AUTHENTICATING,
            ].includes(status);

            // A previous fade-out may still be pending (400ms). If a new
            // reconnect starts inside that window it must be cancelled, or it
            // hides the overlay while we're reconnecting again.
            const cancelPendingHide = () => {
                if (hide_timer) {
                    clearTimeout(hide_timer);
                    hide_timer = null;
                }
            };
            // CONNECTED = back online. DISCONNECTED and AUTHFAIL are only ever
            // set as TERMINAL states (finishDisconnection / auth failure --
            // a retryable drop goes through RECONNECTING instead), so the
            // overlay must not stay stuck over the chat pane in those cases.
            const is_back_or_terminal = [
                Strophe.Status.CONNECTED,
                Strophe.Status.DISCONNECTED,
                Strophe.Status.AUTHFAIL,
            ].includes(status);

            if (is_reconnecting) {
                was_down = true;
                cancelPendingHide();
                overlay.style.display = 'flex';
                overlay.style.opacity = '1';
                if (overlayText) overlayText.textContent = 'Перепідключення...';
            } else if (is_back_or_terminal) {
                // Down -> CONNECTED edge: genuinely back online. Force the
                // catch-up now against a live socket (the OS resume handlers fire
                // before the reconnect completes, so their catch-up can race a
                // still-dead socket). Throttled so flapping VPN/network can't spam
                // it. catchUpNow() runs the anchor sweep AND the anchor-ignoring
                // deep resync, so a stuck anchor can't keep hiding missed history.
                if (was_down && status === Strophe.Status.CONNECTED) {
                    was_down = false;
                    const now = Date.now();
                    if (now - last_recovery_catchup > 10000) {
                        last_recovery_catchup = now;
                        console.log('[Sonata] connection recovered -> forcing MAM catch-up + deep resync');
                        setTimeout(() => {
                            try {
                                api.archive?.catchUpNow?.();
                            } catch (e) {
                                console.warn('recovery catch-up failed', e);
                            }
                        }, 1500);
                    }
                }
                cancelPendingHide();
                overlay.style.opacity = '0';
                hide_timer = setTimeout(() => {
                    overlay.style.display = 'none';
                    hide_timer = null;
                }, 400);
            }
        });
    },
});

// A laptop sleep/resume, screen unlock, or network change kills the XMPP
// connection without the renderer seeing any close/error event — Chromium
// leaves the websocket looking "open" but dead. converse's own ping
// keepalive (converse-ping) would eventually notice and reconnect, but only
// after up to its ping_interval (60s) of silence. electron/main.js notifies
// us the moment the OS actually resumes/unlocks, so ping right away instead
// of waiting — api.ping() reconnects on its own if the ping times out, and
// is a no-op if we're not authenticated, so this is safe to call blindly.
window.electronApp?.onResume(() => {
    try {
        _api?.ping(null, 5000);
        // Same "don't wait out the interval" logic as the ping call above,
        // applied to MAM's periodic catch-up: an OS-level suspend (App Nap
        // included, see electron/main.js's preventAppNap()) can pause that
        // 5-minute interval right along with everything else, so nudge it
        // immediately on resume instead of trusting it picks back up on
        // its own schedule.
        _api?.archive?.catchUpNow?.();
    } catch (e) {
        console.warn('Ping-on-resume failed:', e);
    }
});

// Same dead-but-open-looking websocket problem as above, but from minimizing
// or hiding the window (close-to-tray) rather than an OS sleep -- Chromium
// throttles timers for occluded/hidden windows too, which can delay converse's
// own ping keepalive long enough that a stale connection sits unnoticed while
// minimized. Nothing previously reacted to window:visibility becoming visible
// again (main.js's 'show'/'restore' handlers) other than notification
// suppression (notifications/utils.js) -- messages sent right after restoring
// a long-minimized window could silently go nowhere, with no error, because
// the send just queues on a socket that looks open but never actually
// delivers. Same fix as onResume: ping right away instead of waiting out the
// rest of ping_interval, reconnecting if it turns out to be dead.
window.electronApp?.onWindowVisibilityChange((hidden) => {
    if (hidden) return;
    try {
        _api?.ping(null, 5000);
        _api?.archive?.catchUpNow?.();
    } catch (e) {
        console.warn('Ping-on-restore failed:', e);
    }
});

// Same dead-but-open-looking websocket problem again. Two more signals,
// registered unconditionally (Electron included, NOT just plain browser
// tabs -- electronApp.onResume only covers actual OS sleep/wake via
// powerMonitor; a pure network change (wifi switch, VPN toggle, captive
// portal) with no sleep involved fires neither onResume nor a visibility
// change, in Electron or a browser tab, and was still going undetected
// until the full 60s ping_interval elapsed): the 'online' event (network
// actually restored) and a timer-drift check (a setInterval tick arriving
// far later than scheduled reliably means the event loop -- and the
// socket underneath it -- was frozen and just resumed; catches OS sleep
// even while a tab/window stays visible the whole time, which
// visibilitychange doesn't). That gap is long enough to matter for
// time-sensitive signaling like an incoming call: by the time the stale
// connection is finally noticed and reconnected, the caller's own ring
// timeout has already fired.
{
    const pingNow = (reason) => {
        try {
            _api?.ping(null, 5000);
            _api?.archive?.catchUpNow?.();
        } catch (e) {
            console.warn(`Ping-on-${reason} failed:`, e);
        }
    };

    window.addEventListener('online', () => pingNow('online'));

    const WAKE_CHECK_INTERVAL_MS = 10000;
    const WAKE_DRIFT_THRESHOLD_MS = 20000; // generous slack for normal timer jitter
    let last_tick = Date.now();
    setInterval(() => {
        const now = Date.now();
        const drift = now - last_tick - WAKE_CHECK_INTERVAL_MS;
        last_tick = now;
        if (drift > WAKE_DRIFT_THRESHOLD_MS) pingNow('wake');
    }, WAKE_CHECK_INTERVAL_MS);
}

// Referenced as bare globals by the remaining inline plugins in index.html.
window.CURRENT_CONFIG = CURRENT_CONFIG;
window.updateEndpoints = updateEndpoints;
window.forceLogout = forceLogout;
window.startChat = startChat;
window.clearPersistedCredentials = clearPersistedCredentials;
